import React, { useState, useEffect,useContext, Suspense, lazy, useCallback, memo, useRef } from 'react';
import { Button, Typography, FormControl, RadioGroup, FormControlLabel, Radio, Checkbox } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessAlarm';
import { useHistory } from 'react-router-dom';
import DetectRTC from 'detectrtc';
import swal from 'sweetalert';
import firebase from "firebase/app";
import "firebase/database";
import "./ExamScreen.css";
import DOMPurify from 'dompurify';
import { AuthContext } from './AuthContext';
import ExamSecurity from './ExamSecurity';
import SubmissionProgressModal from './SubmissionProgressModal';
import AuthenticatedImage from './AuthenticatedImage';

const Detection = lazy(() => import('./EnhancedDetections'));

const QuestionItem = memo(({ question, index, answers, handleAnswerChange, isPshycoTest, isAdvancedMCQ }) => {
  return (
    <div className="question-container">
      <h3 className="question-text">
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<pre><code>${index + 1}. ${question.text}</code></pre>`) }} />
      </h3>
      {question.imageUrl && (
        <AuthenticatedImage 
          imageUrl={question.imageUrl}
          questionId={question.id}
          alt="Question" 
          style={{ 
            maxHeight: 650, // 30% larger image for better visibility
            width: '100%'
          }} 
        />
      )}
      {question.problemDescription && (
        <div style={{ 
          marginTop: 8, 
          marginBottom: 12, 
          padding: 12, 
          background: '#f5f5f5', 
          border: '1px solid #ddd',
          borderRadius: 8 
        }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: '#1976d2', fontSize: 14 }}>
            📋 Options and Scenarios:
          </div>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 14 }}>
            {question.problemDescription}
          </div>
        </div>
      )}
      {isPshycoTest === 1 ? (
        <div className="psycho-test-options">
          <input 
            type="text"
            value={answers[index] || ''} 
            onChange={e => handleAnswerChange(index, e.target.value, isPshycoTest)}
            placeholder="Enter A, B, C, D in priority"
            maxLength="4"
          />
        </div>
      ) : isAdvancedMCQ ? (
        <FormControl component="fieldset">
          <RadioGroup
            aria-label={`question-${index}`}
            name={`question-${index}`}
            value={answers[index] || ''}
            onChange={e => handleAnswerChange(index, e.target.value, false)}
          >
            {question.options.map((option, optIndex) => (
              <FormControlLabel
                key={optIndex}
                value={option.text}
                control={<Radio />}
                label={option.text}
              />
            ))}
          </RadioGroup>
        </FormControl>
      ) : (
        <FormControl component="fieldset">
          <RadioGroup
            aria-label={`question-${index}`}
            name={`question-${index}`}
            value={answers[index] || ''}
            onChange={e => handleAnswerChange(index, e.target.value, false)}
          >
            {question.options.map((option, optIndex) => (
              <FormControlLabel key={optIndex} value={option} control={<Radio />} label={option} />
            ))}
          </RadioGroup>
        </FormControl>
      )}
    </div>
  );
});

const ExamScreen = (props) => {
  const securityRef = useRef(null);
  const [count_facedetect, setCountFacedetect] = useState(parseInt(sessionStorage.getItem("count_facedetect") || "0", 10));
  const isLoadTesting = (typeof process !== 'undefined' && process && process.env && process.env.REACT_APP_LOAD_TESTING === '1');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState(parseInt(sessionStorage.getItem("exam_timer")) || 10);
  const [seconds, setSeconds] = useState(parseInt(sessionStorage.getItem("exam_sec")) || 0);
  const history = useHistory();
  const [isPshycoTest,setisPshycoTest] = useState(0);
  const [isAdvancedMCQ, setIsAdvancedMCQ] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submissionInProgress, setSubmissionInProgress] = useState(0);
  const [submissionProgress, setSubmissionProgress] = useState({
    open: false,
    currentStep: 0,
    progress: 0,
    status: 'processing',
    errorMessage: ''
  });
  // const { logErrorToFirebase } = useContext(AuthContext);
  useEffect(() => {
    let isMounted = true; // flag to check if component is mounted

    // Check if we're in load testing mode
    const isLoadTesting = (typeof process !== 'undefined' && process && process.env && process.env.REACT_APP_LOAD_TESTING === '1');

    // Initialize security system only if not in load testing mode
    if (!isLoadTesting) {
      securityRef.current = new ExamSecurity();
      securityRef.current.init({
        webcamRequired: true,
        fullscreenRequired: true,
        onCheatDetected: (type, count, message) => {
          if (isMounted && !submissionInProgress) {
            // Cheat detected - handled by security system
          }
        },
        onFullscreenExit: () => {
          if (isMounted && !submissionInProgress) {
            history.push("/enhanced-fullscreen-alert");
          }
        }
      });
    }

    // Fetch exam data
    const fetchExamData = async () => {
      // Check if user is authenticated before accessing Firebase
      const currentUser = firebase.auth().currentUser;
      if (!currentUser && !isLoadTesting) {
        console.error("User not authenticated");
        swal("Authentication Error", "Please log in to access the assessment.", "error");
        history.push("/login");
        return;
      }

      const examCode = sessionStorage.getItem("formvalid");
      if (examCode) {
        const examRef = firebase.database().ref(`TestPapers/${examCode}`);
        const snapshot = await examRef.once('value');
        const data = snapshot.val();
        if (isMounted && data) {
          if (data.isPshyco === 1) {
            setisPshycoTest(1);
          } else if (data.isPshyco === 3) {
            setIsAdvancedMCQ(true);
          }
          const loadedQuestions = data.questions || [];
          const shuffledQuestions = shuffleArray(loadedQuestions); // Shuffle only once when data is fetched
          setQuestions(shuffledQuestions); // Set the shuffled questions
          setLoading(false);
        } else if (isMounted) {
          swal("Error", "No assessment data found!", "error");
          history.push('/error');
        }
      } else if (isMounted) {
        swal("Error", "Invalid assessment code!", "error");
        history.push('/error');
      }
    };

    fetchExamData();

    // Timer logic
    const myInterval = setInterval(() => {
      if (isMounted) {
        setSeconds((prevSeconds) => {
          if (prevSeconds > 0) {
            const newSeconds = prevSeconds - 1;
            sessionStorage.setItem("exam_sec", newSeconds);
            return newSeconds;
          } else if (minutes > 0) {
            // Transition to the next minute
            setMinutes((prevMinutes) => {
              const newMinutes = prevMinutes - 1;
              sessionStorage.setItem("exam_timer", newMinutes);
              sessionStorage.setItem("exam_sec", 59); // Reset seconds
              return newMinutes;
            });
            return 59; // Reset seconds to 59 for the new minute
          } else {
            // Timer is up - prevent negative values
            clearInterval(myInterval);
            sessionStorage.setItem("exam_timer", 0);
            sessionStorage.setItem("exam_sec", 0);
            history.push('/thankyou');
            return 0; // Stop timer
          }
        });
      }
    }, 1000);

    // DetectRTC logic for webcam detection (skip in load testing)
    if (!isLoadTesting) {
      DetectRTC.load(() => {
        const webcam = DetectRTC.isWebsiteHasWebcamPermissions;
        if (!webcam) {
          navigator.mediaDevices.getUserMedia({ video: true })
            .then((stream) => {
              document.querySelector("#videoElement").srcObject = stream;
            })
            .catch((err) => {
            });
        }
      });
    }

    // Cleanup all event listeners and interval on component unmount
    return () => {
      isMounted = false; // Set to false when the component unmounts
      if (securityRef.current) {
        securityRef.current.cleanup();
      }
      clearInterval(myInterval);
    };
  }, []);

  const shuffleArray = (array) => {
    // Map each question to include its original index
    const indexedArray = array.map((question, index) => ({
        ...question,
        originalIndex: index, // Store the original index
    }));

    // Shuffle the indexed array
    return indexedArray.sort(() => Math.random() - 0.5);
  };

  const handleAnswerChange = useCallback((questionIndex, option, isPshycoTest) => {
    if(isPshycoTest){
      const sanitizedValue = option.toUpperCase().replace(/[^ABCD]/g, '').slice(0, 4);
      setAnswers(prevAnswers => ({ ...prevAnswers, [questionIndex]: sanitizedValue }));
    } else {
      setAnswers(prevAnswers => ({ ...prevAnswers, [questionIndex]: option }));
    }
  }, []);

  const handleDetectionAlert = (type, message) => {
    if(!submissionInProgress){
      // Record the violation in the security system
      if (securityRef.current) {
        securityRef.current.recordDetectionViolation(type, message);
      }
    }
  };

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const unansweredQuestions = questions.map((question, index) => {
        return !answers[index] ? index + 1 : null;
    }).filter(q => q !== null);

    if (unansweredQuestions.length > 0) {
      swal({
        title: "Complete all assessment items",
        text: `The following items require responses: ${unansweredQuestions.join(", ")}`,
        icon: "warning",
      });
      return;
    }

    if (isPshycoTest === 1) {
      const shortAnswers = questions.map((question, index) => {
        return answers[index] && answers[index].length < 4 ? index + 1 : null;
      }).filter(q => q !== null);
      if (shortAnswers.length > 0) {
        swal({
          title: "Review Assessment Responses",
          text: `Ensure all 4 options (A, B, C, D) are provided in priority order for the following items: ${shortAnswers.join(", ")}`,
          icon: "warning",
        });
        return;
      }
    }

    swal({
      id: "sureAlertExamScreen",
      title: "Are you sure?",
      text: "Do you want to submit your answers?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willSubmit) => {
      if (willSubmit) {
        // Pause security alerts during submission
        if (securityRef.current) {
          securityRef.current.pauseSecurityAlerts();
        }

        // Show submission progress modal
        setSubmissionProgress({
          open: true,
          currentStep: 0,
          progress: 0,
          status: 'processing',
          errorMessage: ''
        });

        setSubmissionInProgress(true);
        if (isPshycoTest === 1) {
          calculateAndSaveResultForPshycoTest();
        } else if (isAdvancedMCQ) {
          calculateAndSaveResultForMCQ();
        } else {
          calculateAndSaveResult();
        }
      }
    });
  }, [questions, answers, isPshycoTest, isAdvancedMCQ]);

  function createAlertContent(messageParts) {
    const div = document.createElement("div");
    div.style.textAlign = "left";
    div.style.fontSize = "16px";
    div.style.lineHeight = "1.6";
    div.innerHTML = messageParts.map(part => `<div style="margin-bottom: 20px;">${part}</div>`).join("");
    return div;
  }
  function generateRandomString(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }
  const calculateAndSaveResultForPshycoTest = () => {
    // Show loader while saving result
    swal({
        title: "Submitting...",
        text: "Please wait while we save your result.",
        icon: "info",
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
    });

    const examCode = sessionStorage.getItem("formvalid");
    const userEmail = sanitizeEmail(sessionStorage.getItem("checkemail"));
    const count_facedetect = sessionStorage.getItem("count_facedetect");
    const count_fullscreen = sessionStorage.getItem("count_fullscreen");
    const count_tabchange = sessionStorage.getItem("count_tabchange");
    const countalt = sessionStorage.getItem("countalt");
    const countctrl = sessionStorage.getItem("countctrl");
    const count_copypaste = sessionStorage.getItem("count_copypaste");
    const count_webcamoff = sessionStorage.getItem("count_webcamoff");
    const count_contextmenu = sessionStorage.getItem("count_contextmenu");
    const count_noPerson = sessionStorage.getItem("count_noPerson");
    const count_multiplePersons = sessionStorage.getItem("count_multiplePersons");
    const count_suspiciousObjects = sessionStorage.getItem("count_suspiciousObjects");
    const checkn = sessionStorage.getItem("checkname");
    const checke = sessionStorage.getItem("checkemail");
    const photo = sessionStorage.getItem("imageSrc");

    // Reference to the exam data
    const examRef = firebase.database().ref(`TestPapers/${examCode}/questions`);

    examRef.once('value', snapshot => {
        const questionsData = snapshot.val();
        if (!questionsData) {
            swal.close(); // Close the loader in case of error
            swal("Error", "No questions found for this assessment.", "error");
            setSubmissionInProgress(false);
            return;
        }

        let totalScore = 0;
        let sectionScores = [];
        const sectionSize = 5; // Each section contains 5 questions
        const sectionMarksMap = {};
        const questionResults = {};

        // Calculate the score by comparing answers with correct options
        questions.forEach((question, index, originalIndex) => {
            if (questionsData[question.originalIndex] && questionsData[question.originalIndex].correctOption) {
                const correctOption = questionsData[question.originalIndex].correctOption.trim().toUpperCase();
                const userAnswer = answers[index].trim().toUpperCase();
                const correctOptionArray = correctOption.split('');
                const userAnswerArray = userAnswer.split('');
                const marks = calculateMarksBasedOnPattern(correctOptionArray, userAnswerArray);
                totalScore += marks;

                // Determine which section this question belongs to
                const sectionIndex = Math.floor(question.originalIndex / sectionSize);
                if (!sectionMarksMap[sectionIndex]) {
                    sectionMarksMap[sectionIndex] = 0;
                }
                sectionMarksMap[sectionIndex] += marks;

                questionResults[index] = {
                    originalIndex: question.originalIndex,
                    userAnswer: answers[index],
                    marks: marks,
                };
            }
        });

        sectionScores = Object.keys(sectionMarksMap)
            .sort((a, b) => a - b)
            .map(sectionIndex => sectionMarksMap[sectionIndex]);

        const uniqueUserRef = `Exam_results/${examCode}/${userEmail}`;

        // Retry logic
        const maxRetries = 3;
        let attempt = 0;

        const saveResult = () => {
            // Update progress
            setSubmissionProgress(prev => ({
                ...prev,
                currentStep: 1,
                progress: 50
            }));

            // For load testing, save to separate collection
            if (isLoadTesting) {
                const userEmail = sessionStorage.getItem("checkemail") || 'load-test-user';
                const sanitizedEmail = userEmail.replace(/\./g, ',');
                const uniqueEmail = `${sanitizedEmail}_${Date.now()}`;
                const loadTestRef = firebase.database().ref(`Load_Testing/${examCode}/${uniqueEmail}`);
                const loadTestData = {
                    examCode,
                    answers: answers,
                    score: totalScore,
                    totalQuestions: questions.length,
                    sectionScores: sectionScores,
                    questionResults: questionResults,
                    alt: countalt,
                    ctrl: countctrl,
                    tab: count_tabchange,
                    face: count_facedetect,
                    fullscreen: count_fullscreen,
                    copyPaste: count_copypaste,
                    webcamOff: count_webcamoff,
                    contextMenu: count_contextmenu,
                    noPerson: count_noPerson,
                    multiplePersons: count_multiplePersons,
                    suspiciousObjects: count_suspiciousObjects,
                    totalCheatCount: (parseInt(count_copypaste) + parseInt(count_tabchange) + parseInt(count_webcamoff)),
                    semail: checke,
                    sname: checkn,
                    photo: photo,
                    isLoadTest: true,
                    loadTestTimestamp: Date.now()
                };

                loadTestRef.set(loadTestData)
                .then(() => {
                    setSubmissionProgress(prev => ({
                        ...prev,
                        currentStep: 2,
                        progress: 100,
                        status: 'success'
                    }));

                    setTimeout(() => {
                        setSubmissionProgress(prev => ({ ...prev, open: false }));
                        history.push('/thankyou');
                        setSubmissionInProgress(false);
                    }, 2000);
                })
                .catch(error => {
                    setSubmissionProgress(prev => ({
                        ...prev,
                        status: 'error',
                        errorMessage: 'Load testing submission failed.'
                    }));

                    setTimeout(() => {
                        setSubmissionProgress(prev => ({ ...prev, open: false }));
                        setSubmissionInProgress(false);
                    }, 3000);
                });
                return;
            }

            firebase.database().ref(uniqueUserRef).set({
                answers: answers,
                score: totalScore,
                totalQuestions: questions.length,
                sectionScores: sectionScores,
                questionResults: questionResults,
                alt: countalt,
                ctrl: countctrl,
                tab: count_tabchange,
                face: count_facedetect,
                fullscreen: count_fullscreen,
                copyPaste: count_copypaste,
                webcamOff: count_webcamoff,
                contextMenu: count_contextmenu,
                noPerson: count_noPerson,
                multiplePersons: count_multiplePersons,
                suspiciousObjects: count_suspiciousObjects,
                totalCheatCount: (parseInt(count_copypaste) + parseInt(count_tabchange) + parseInt(count_webcamoff)),
                semail: checke,
                sname: checkn,
                photo: photo
            })
            .then(() => {
                // Update progress to completion
                setSubmissionProgress(prev => ({
                    ...prev,
                    currentStep: 2,
                    progress: 100,
                    status: 'success'
                }));

                // Resume security alerts
                if (securityRef.current) {
                    securityRef.current.resumeSecurityAlerts();
                }

                setTimeout(() => {
                    setSubmissionProgress(prev => ({ ...prev, open: false }));
                    history.push('/thankyou');
                    setSubmissionInProgress(false);
                }, 2000);
            })
            .catch(error => {
                // Resume security alerts on error
                if (securityRef.current) {
                    securityRef.current.resumeSecurityAlerts();
                }

                setSubmissionProgress(prev => ({
                    ...prev,
                    status: 'error',
                    errorMessage: 'Failed to submit your answers. Please try again.'
                }));

                setTimeout(() => {
                    setSubmissionProgress(prev => ({ ...prev, open: false }));
                    setSubmissionInProgress(false);
                }, 3000);
            });
        };

        saveResult(); // Initial save operation
    })
    .catch(error => {
        swal.close(); // Close the loader if there's an error fetching the questions
        // logErrorToFirebase(`Error fetching questions for exam: ${examCode}. Error: ${error.message}`);
        swal("Error", "Unable to fetch assessment questions. Please try again.", "error");
        setSubmissionInProgress(false);
    });
};

  function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    for (var i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
    }
    return true; 
  }
  const calculateMarksBasedOnPattern = (bestPattern, selectedOptions) => {
    let marks = 0;
    
    // Ensure the selected option has the same length as the best pattern
    if (selectedOptions.length !== bestPattern.length) {
        return marks; // Return 0 marks if lengths don't match
    }

    // Check for exact match (Perfect Match)
    if (arraysEqual(selectedOptions, bestPattern)) {
        marks = 5; // ABCD - Perfect match, assign 5 marks
    }
    // Check for second best match (BACD)
    else if (selectedOptions[0] === bestPattern[1] && selectedOptions[1] === bestPattern[0] && 
             selectedOptions[2] === bestPattern[2] && selectedOptions[3] === bestPattern[3]) {
        marks = 4; // BACD - Second best match, assign 4 marks
    }
    // Check for partial matches where A & B are in 1st & 3rd positions, and D remains last (ACBD, BCAD)
    else if (
        (selectedOptions[0] === bestPattern[0] && selectedOptions[2] === bestPattern[1] && selectedOptions[3] === bestPattern[3]) || 
        (selectedOptions[0] === bestPattern[1] && selectedOptions[2] === bestPattern[0] && selectedOptions[3] === bestPattern[3])
    ) {
        marks = 2.5; // ACBD, BCAD - Partial match, assign 2.5 marks
    }
    // Check if A & B are in 1st & 2nd, D in 3rd, and C in last position (ABDC)
    else if (
        selectedOptions[0] === bestPattern[0] && selectedOptions[1] === bestPattern[1] && 
        selectedOptions[3] === bestPattern[2] && selectedOptions[2] === bestPattern[3]
    ) {
        marks = 1; // ABDC - Assign 1 mark
    }
    // Check if B & A are in 1st & 2nd positions, D is forward, and C remains last (BADC)
    else if (
        selectedOptions[0] === bestPattern[1] && selectedOptions[1] === bestPattern[0] && 
        selectedOptions[3] === bestPattern[2] && selectedOptions[2] === bestPattern[3]
    ) {
        marks = 1; // BADC - Assign 1 mark
    }
    // Check if C is first, A & B are in 2nd & 3rd, and D is last (CABD, CBAD)
    else if (
        selectedOptions[0] === bestPattern[2] && 
        ((selectedOptions[1] === bestPattern[0] && selectedOptions[2] === bestPattern[1]) || 
         (selectedOptions[1] === bestPattern[1] && selectedOptions[2] === bestPattern[0])) && 
        selectedOptions[3] === bestPattern[3]
    ) {
        marks = 1.5; // CABD, CBAD - Assign 1.5 marks
    }
    // Check if C and D are given more preference (CADB, CBDA, CDAB, CDBA)
    else if (selectedOptions[0] === bestPattern[2] || selectedOptions[0] === bestPattern[3]) {
        marks = 0; // Assign 0 marks
    }
    // Check if D is in the 1st position (DCAB, DCBA, DABC, DBAC, DACB, DBCA)
    else if (selectedOptions[0] === bestPattern[3]) {
        marks = 0; // Assign 0 marks
    }

    return marks;
  };

  
  const calculateAndSaveResult = () => {
    // Show loader while saving result
    swal({
        title: "Submitting...",
        text: "Please wait while we save your result.",
        icon: "info",
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
    });

    const examCode = sessionStorage.getItem("formvalid");
    const userEmail = sanitizeEmail(sessionStorage.getItem("checkemail"));
    const count_facedetect = sessionStorage.getItem("count_facedetect");
    const count_fullscreen = sessionStorage.getItem("count_fullscreen");
    const count_tabchange = sessionStorage.getItem("count_tabchange");
    const countalt = sessionStorage.getItem("countalt");
    const countctrl = sessionStorage.getItem("countctrl");
    const checkn = sessionStorage.getItem("checkname");
    const checke = sessionStorage.getItem("checkemail");
    const photo = sessionStorage.getItem("imageSrc");

    // Reference to the exam data
    const examRef = firebase.database().ref(`TestPapers/${examCode}/questions`);

    examRef.once('value', snapshot => {
        const questionsData = snapshot.val();
        if (!questionsData) {
            swal.close(); // Close the loader in case of error
            swal("Error", "No questions found for this assessment.", "error");
            setSubmissionInProgress(false);
            return;
        }

        let totalScore = 0;
        let sectionScores = [];
        const sectionSize = 5; // Each section contains 5 questions
        const sectionMarksMap = {};
        const questionResults = {};
        // Calculate the score by comparing answers with correct options
        questions.forEach((question, index, originalIndex) => {
          if (questionsData[question.originalIndex] && questionsData[question.originalIndex].correctOption) {
              const correctOption = questionsData[question.originalIndex].correctOption.trim().toUpperCase();
              const userAnswer = answers[index].trim().toUpperCase();
              const marks = (userAnswer === correctOption) ? 1 : 0;
              totalScore += marks;

              // Determine which section this question belongs to
              const sectionIndex = Math.floor(question.originalIndex / sectionSize);
              if (!sectionMarksMap[sectionIndex]) {
                  sectionMarksMap[sectionIndex] = 0;
              }
              sectionMarksMap[sectionIndex] += marks;

              questionResults[index] = {
                  originalIndex: question.originalIndex,
                  userAnswer: answers[index],
                  marks: marks,
              };
          }
      });
        sectionScores = Object.keys(sectionMarksMap)
        .sort((a, b) => a - b)
        .map(sectionIndex => sectionMarksMap[sectionIndex]);
        const uniqueUserRef = `Exam_results/${examCode}/${userEmail}`;

        // Retry logic
        const maxRetries = 3;
        let attempt = 0;

        const saveResult = () => {
            firebase.database().ref(uniqueUserRef).set({
                answers: answers,
                score: totalScore,
                totalQuestions: questions.length,
                sectionScores: sectionScores,
                questionResults: questionResults,
                alt: countalt,
                ctrl: countctrl,
                tab: count_tabchange,
                face: count_facedetect,
                fullscreen: count_fullscreen,
                copyPaste: sessionStorage.getItem("count_copypaste"),
                webcamOff: sessionStorage.getItem("count_webcamoff"),
                contextMenu: sessionStorage.getItem("count_contextmenu"),
                noPerson: sessionStorage.getItem("count_noPerson"),
                multiplePersons: sessionStorage.getItem("count_multiplePersons"),
                suspiciousObjects: sessionStorage.getItem("count_suspiciousObjects"),
                totalCheatCount: (parseInt(sessionStorage.getItem("count_copypaste")) + parseInt(count_tabchange) + parseInt(sessionStorage.getItem("count_webcamoff"))),
                semail: checke,
                sname: checkn,
                photo: photo
            })
            .then(() => {
                // Log success message
                // logErrorToFirebase(`Successfully saved data for email: ${checke}`);

                // Close loader and show success message
                swal("Success", "Your answers have been submitted successfully!", "success").then(() => {
                    // Redirect to the thank you page only after Firebase success
                    swal.close();
                    history.push('/thankyou');
                    setSubmissionInProgress(false);
                });
            })
            .catch(error => {
                // Log error message
                // logErrorToFirebase(`Error while saving data for email: ${checke}. Attempt ${attempt + 1} failed: ${error.message}`);

                // Retry if less than maxRetries
                if (attempt < maxRetries) {
                    attempt++;
                    saveResult(); // Retry the save operation
                } else {
                    swal.close(); // Close the loader
                    swal("Error", "Failed to submit your answers after multiple attempts. Please try again.", "error");
                    setSubmissionInProgress(false);
                }
            });
        };

        saveResult(); // Start the initial save attempt

    }).catch(error => {
        // Handle error if there's an issue fetching the questions
        // logErrorToFirebase(`Error while fetching questions for exam: ${examCode}. Error: ${error.message}`);

        swal.close(); // Close the loader if there's an error fetching the questions
        swal("Error", "Unable to fetch assessment questions. Please try again.", "error");
        setSubmissionInProgress(false);
    });
};

  const calculateAndSaveResultForMCQ = () => {
    swal({
      title: "Submitting...",
      text: "Please wait while we save your result.",
      icon: "info",
      buttons: false,
      closeOnClickOutside: false,
      closeOnEsc: false,
    });

    const examCode = sessionStorage.getItem("formvalid");
    const userEmail = sanitizeEmail(sessionStorage.getItem("checkemail"));
    const count_facedetect = sessionStorage.getItem("count_facedetect");
    const count_fullscreen = sessionStorage.getItem("count_fullscreen");
    const count_tabchange = sessionStorage.getItem("count_tabchange");
    const countalt = sessionStorage.getItem("countalt");
    const countctrl = sessionStorage.getItem("countctrl");
    const checkn = sessionStorage.getItem("checkname");
    const checke = sessionStorage.getItem("checkemail");
    const photo = sessionStorage.getItem("imageSrc");

    const examRef = firebase.database().ref(`TestPapers/${examCode}/questions`);
    examRef.once('value', snapshot => {
      const questionsData = snapshot.val();
      if (!questionsData) {
        swal("Error", "No questions found for this assessment.", "error");
        setSubmissionInProgress(false);
        return;
      }
      let totalScore = 0;
      const questionResults = {};
      questions.forEach((question, index) => {
        const userAnswer = answers[index] || null;
        let questionScore = 0;
        if (userAnswer && questionsData[question.originalIndex] && questionsData[question.originalIndex].options) {
          const allOptions = questionsData[question.originalIndex].options;
          const selectedOption = allOptions.find(opt => opt.text === userAnswer);
          if (selectedOption && selectedOption.isCorrect) {
            questionScore = selectedOption.marks;
          }
        }
        totalScore += questionScore;
        questionResults[index] = {
          originalIndex: question.originalIndex,
          userAnswer: userAnswer,
          marks: questionScore,
        };
      });

      const uniqueUserRef = `Exam_results/${examCode}/${userEmail}`;
      firebase.database().ref(uniqueUserRef).set({
        answers: answers,
        score: totalScore,
        totalQuestions: questions.length,
        questionResults: questionResults,
        alt: countalt,
        ctrl: countctrl,
        tab: count_tabchange,
        face: count_facedetect,
        fullscreen: count_fullscreen,
        copyPaste: sessionStorage.getItem("count_copypaste"),
        webcamOff: sessionStorage.getItem("count_webcamoff"),
        contextMenu: sessionStorage.getItem("count_contextmenu"),
        noPerson: sessionStorage.getItem("count_noPerson"),
        multiplePersons: sessionStorage.getItem("count_multiplePersons"),
        suspiciousObjects: sessionStorage.getItem("count_suspiciousObjects"),
        totalCheatCount: (parseInt(sessionStorage.getItem("count_copypaste")) + parseInt(count_tabchange) + parseInt(sessionStorage.getItem("count_webcamoff"))),
        semail: checke,
        sname: checkn,
        photo: photo
      })
      .then(() => {
        swal("Success", "Your answers have been submitted successfully!", "success").then(() => {
          history.push('/thankyou');
          setSubmissionInProgress(false);
        });
      })
      .catch(error => {
        swal("Error", "Failed to submit your answers. Please try again.", "error");
        setSubmissionInProgress(false);
      });
    });
  };

  const sanitizeEmail = (email) => email.replace(/\./g, ',');
  // const shuffledQuestions = shuffle(questions); 
  return (
    <div className="dashboard-container">
      {/* Submission Progress Modal */}
      <SubmissionProgressModal
        open={submissionProgress.open}
        onClose={() => setSubmissionProgress(prev => ({ ...prev, open: false }))}
        steps={[
          'Reviewing your brilliant responses...',
          'Securing everything safely...',
          'Almost there...'
        ]}
        currentStep={submissionProgress.currentStep}
        progress={submissionProgress.progress}
        status={submissionProgress.status}
        errorMessage={submissionProgress.errorMessage}
      />
      <div className="left-panel">
        {!isLoadTesting && (
          <div className="detect">
            <Suspense fallback={<div>Loading Webcam...</div>}>
              <Detection onCheatDetected={handleDetectionAlert} />
            </Suspense>
          </div>
        )}
        <div className="lame">
          <p style={{ margin: '5px' }} id="nameTxt">Name: {sessionStorage.getItem("checkname")}</p>
          <p style={{ margin: '5px' }} id="emailTxt">Email: {sessionStorage.getItem("checkemail")}</p>
        </div>
        <div className="timerContainer">
          <Typography className="labelText">Remaining Time</Typography>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon className="icon" />
            <Typography className="timerText">
              {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
            </Typography>
          </div>
        </div>
        <div className="instructionsContainer">
          <Typography variant="h6" align="center" id="instructionsTxt" className="instructionsHeader">
            Assessment Guidelines
          </Typography>
          <Typography variant="body2" align="left" className="instructionsText">
            <ul>
              <li><strong>Data Preservation:</strong> Ensure all responses are properly saved before concluding the session.</li>
              <li><strong>Session Integrity:</strong> Maintain browser session - premature exit may result in data loss.</li>
            </ul>
          </Typography>
        </div>
      </div>
      <div className="right-panel">
        <h1 id="headTextExamScreen">Assessment Items</h1>
        <form onSubmit={handleSubmit} className="exam-form">
          <div className="questions-list">
            {questions.map((question, index) => (
              <QuestionItem
                key={question.originalIndex}
                question={question}
                index={index}
                answers={answers}
                handleAnswerChange={handleAnswerChange}
                isPshycoTest={isPshycoTest}
                isAdvancedMCQ={isAdvancedMCQ}
              />
            ))}
          </div>
          <Button type="submit" variant="contained" color="primary" className="submit-button">
            Submit Assessment
          </Button>
        </form>
      </div>
    </div>
  );
}

export default ExamScreen;
